import styled, { css } from 'styled-components';
import Typography from '../../../../../styles/Typography';
import { StyledDefaultRadioProps } from '../../types';

const hexToRgba = (hex: string, alpha: number): string => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((hex || '').trim());
  if (!m) return `rgba(0,0,0,${alpha})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`;
};

export const StyledRadioWrapper = styled.span`
  display: block;
  width: 100%;
`;

export const StyledRadioControlWrapper = styled.span`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.25rem 0.5rem 0.5rem;
  cursor: pointer;
  margin-bottom: 0.125rem;
`;

export const StyledRadioMark = styled.span<StyledDefaultRadioProps>`
  display: none;
`;

export const StyledRadioLabel = styled(Typography).attrs({
  forwardedAs: 'span',
  variant: 'body',
})`
  display: inline-flex;
  margin-left: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledRadioDecorWrapper = styled.figure`
  display: inline-flex;
  height: 1.375rem;
  width: 1.375rem;
  flex-shrink: 0;
  border-radius: 0.3125rem;
  background-color: var(--paper);
  border: 1px solid rgba(9, 15, 56, 0.08);
  overflow: hidden;
  justify-content: center;
  align-items: center;
  margin-left: 0.375rem;
`;

export const StyledRadioDecor = styled.img`
  height: auto;
  width: 100%;
  max-width: 100%;
  display: block;
`;

export const StyledRadioCheckboxesList = styled.ul`
  list-style: none;
  padding: 0.25rem 0 0;
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

interface ChipItemProps {
  tagColor?: string;
  isChecked?: boolean;
}

export const StyledRadioCheckboxesListItem = styled.li<ChipItemProps>`
  position: relative;
  border-radius: var(--r-sm);
  cursor: pointer;
  transition: background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease;

  & label {
    cursor: pointer;
    border-radius: inherit;
    display: flex;
    align-items: center;
  }

  & label span {
    font-size: 0.8125rem;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--ink);
  }

  /* Slim row mode (no tagColor — workspace entity switcher) */
  ${({ tagColor, isChecked }) =>
    !tagColor &&
    css`
      background: ${isChecked ? 'rgba(118,177,89,0.07)' : 'transparent'};

      & label {
        padding: 0.375rem 0.5rem;
        padding-right: ${isChecked ? '1.75rem' : '0.5rem'};
      }

      & label > div:first-of-type {
        display: none;
      }
      & label > div:first-of-type + div {
        margin-left: 0;
      }

      & label span {
        color: ${isChecked ? 'var(--ink)' : 'rgba(9,15,56,0.6)'};
        font-weight: ${isChecked ? '600' : '400'};
      }

      ${isChecked &&
      css`
        &::after {
          content: '✓';
          position: absolute;
          right: 0.5rem;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--brand-green);
          pointer-events: none;
        }
      `}

      &:hover {
        background: rgba(118, 177, 89, 0.05);
        & label span {
          color: var(--ink);
        }
      }
    `}

  /* Chip mode (tagColor present — status filter) */
  ${({ tagColor, isChecked }) =>
    tagColor &&
    css`
      border: 1.5px solid
        ${isChecked ? tagColor : hexToRgba(tagColor, 0.08)};
      background: ${isChecked
        ? `linear-gradient(135deg, ${hexToRgba(tagColor, 0.16)}, ${hexToRgba(tagColor, 0.06)})`
        : 'var(--surface)'};
      ${isChecked &&
      css`
        box-shadow: 0 4px 12px -4px ${hexToRgba(tagColor, 0.32)},
          inset 0 0 0 1px ${hexToRgba(tagColor, 0.18)};
        transform: translateY(-1px);
      `}

      &:hover {
        transform: translateY(-1px);
        border-color: ${tagColor};
        background: ${isChecked
          ? `linear-gradient(135deg, ${hexToRgba(tagColor, 0.22)}, ${hexToRgba(tagColor, 0.1)})`
          : hexToRgba(tagColor, 0.05)};
        box-shadow: 0 4px 12px -4px ${hexToRgba(tagColor, 0.22)};
      }

      & label {
        padding: 0.625rem 0.875rem;
      }

      & label > div:first-of-type {
        display: none;
      }
      & label > div:first-of-type + div {
        margin-left: 0;
      }

      & label [class*='StyledColorTag'],
      & label > span > div:first-child {
        width: 0.75rem !important;
        height: 0.75rem !important;
        box-shadow: 0 0 0 4px
          ${isChecked ? hexToRgba(tagColor, 0.25) : hexToRgba(tagColor, 0.12)};
        transition: box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1);
      }

      & label span {
        font-weight: 600;
        color: ${isChecked ? tagColor : 'var(--ink)'};
      }

      ${isChecked &&
      css`
        &::after {
          content: '✓';
          position: absolute;
          right: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.875rem;
          font-weight: 700;
          color: ${tagColor};
        }
      `}
    `}
`;
